import React from 'react';
import { workProcess } from '../mockData';
import { Search, Calculator, Beaker, TestTube, Rocket, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const HowWeWork = () => {
  const iconMap = {
    Search: Search,
    Calculator: Calculator,
    Beaker: Beaker,
    TestTube: TestTube,
    Rocket: Rocket
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              How We Work
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              A proven process to transform your recipe into a market-ready product
            </p>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="space-y-16">
            {workProcess.map((step, index) => {
              const Icon = iconMap[step.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={step.step} className="relative">
                  {/* Connector Line */}
                  {index < workProcess.length - 1 && (
                    <div className="absolute left-1/2 top-32 w-0.5 h-24 bg-emerald-300 transform -translate-x-1/2 hidden lg:block" />
                  )}
                  
                  <div className={`flex flex-col lg:flex-row items-center gap-8 ${isEven ? '' : 'lg:flex-row-reverse'}`}>
                    {/* Content Side */}
                    <div className={`flex-1 ${isEven ? 'lg:text-right' : 'lg:text-left'}`}>
                      <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300">
                        <div className={`text-emerald-600 font-bold text-sm mb-2 ${isEven ? 'lg:text-right' : 'lg:text-left'}`}>
                          STEP {step.step}
                        </div>
                        <h3 className="text-3xl font-bold text-gray-900 mb-4">
                          {step.title}
                        </h3>
                        <p className="text-gray-700 text-lg leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {/* Icon Circle */}
                    <div className="flex-shrink-0">
                      <div className="w-24 h-24 bg-emerald-600 rounded-full flex items-center justify-center shadow-xl">
                        <Icon size={40} className="text-white" />
                      </div>
                    </div>

                    {/* Empty Space for alignment */}
                    <div className="flex-1 hidden lg:block" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Timeline Summary */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Typical Project Timeline
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From initial consultation to market launch
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            <div className="bg-white p-6 rounded-none shadow-lg text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">1-2</div>
              <div className="text-gray-900 font-semibold mb-2">Weeks</div>
              <div className="text-gray-600 text-sm">Discovery & Review</div>
            </div>
            <div className="bg-white p-6 rounded-none shadow-lg text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">2-3</div>
              <div className="text-gray-900 font-semibold mb-2">Weeks</div>
              <div className="text-gray-600 text-sm">Feasibility Analysis</div>
            </div>
            <div className="bg-white p-6 rounded-none shadow-lg text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">3-4</div>
              <div className="text-gray-900 font-semibold mb-2">Weeks</div>
              <div className="text-gray-600 text-sm">Prototype Development</div>
            </div>
            <div className="bg-white p-6 rounded-none shadow-lg text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">2-3</div>
              <div className="text-gray-900 font-semibold mb-2">Weeks</div>
              <div className="text-gray-600 text-sm">Pilot Manufacturing</div>
            </div>
            <div className="bg-white p-6 rounded-none shadow-lg text-center">
              <div className="text-4xl font-bold text-emerald-600 mb-2">Ongoing</div>
              <div className="text-gray-900 font-semibold mb-2">Support</div>
              <div className="text-gray-600 text-sm">Scale & Launch</div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Timelines may vary based on product complexity, regulatory requirements, and testing needs. 
              We work flexibly to meet your launch deadlines while maintaining quality standards.
            </p>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Makes Us Different
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our unique approach to food manufacturing partnerships
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-emerald-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cultural Expertise</h3>
              <p className="text-gray-700">
                Deep understanding of traditional Indian and Telugu cuisine, ensuring authentic flavors and methods are preserved during scaling.
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-blue-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Flexible Partnership</h3>
              <p className="text-gray-700">
                We adapt to your brand's stage - from initial concept to established products needing production support.
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-emerald-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality Focus</h3>
              <p className="text-gray-700">
                Rigorous quality standards with HACCP certification and complete traceability throughout the production process.
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-blue-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cost Optimization</h3>
              <p className="text-gray-700">
                Strategic ingredient sourcing and production planning to keep costs competitive without compromising quality.
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-emerald-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Local Network</h3>
              <p className="text-gray-700">
                Birmingham-based with strong connections to UK food entrepreneurs, retailers, and distribution partners.
              </p>
            </div>

            <div className="bg-white p-8 rounded-none shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-blue-600">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">End-to-End Support</h3>
              <p className="text-gray-700">
                Beyond manufacturing - we support packaging, compliance, and market entry to ensure your success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Start Your Journey
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Schedule a consultation to discuss your recipe and manufacturing needs
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-10 py-5 rounded-none font-bold text-lg transition-all duration-300 hover:bg-gray-100 hover:shadow-2xl hover:-translate-y-1"
          >
            Book Consultation
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HowWeWork;
